import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"

// «Finanzia il tuo progetto» (pages/TrovaBando.js, 31a5bd1), passi 0 e 1. Lo stato vive qui come nei componenti React:
// step, profile (il profilo passato al passo 1), estratto (il profilo letto dal testo, per la fonte dei dati),
// sourceNote; InputStep (modo, testo, attesa, errore, ultimo file) e ProfileStep (p, progetti, daProgetto) ripartono
// da capo a ogni ingresso nel loro passo, come il remount dell'originale. «Cerca i bandi» passa al passo 2 (Task 49)
// con il profilo confermato: JSON in finder-profilo-confermato ed evento "trova:profilo-confermato" (detail.profile).
// I testi dell'AI e dei progetti entrano nella pagina solo come testo o valore (mai HTML). Dal passo 2 «Correggi
// profilo» riapre il passo 1 con il profilo confermato e «Nuova analisi» riparte da capo, impresa compresa.

const STAGE_NUOVO = { "prototipo-iniziale": "early", "prototipo-validato": "early", "mvp-ricavi": "growth" }
const triDa = (v) => (v === true ? "si" : v === false ? "no" : "nd")
const triA = (v) => (v === "si" ? true : v === "no" ? false : null)
export const formaDa = (costituita, startup) => (costituita !== "si" ? "team-non-costituito" : startup === "si" ? "startup-innovativa" : "azienda-consolidata")
const SPESA_DA_IDEA = { digitalizzazione: ["digitalizzazione", "software"], investimenti: ["macchinari"], brevetti_ip: ["brevetto"],
  formazione: ["formazione"], export: ["export"], ricerca_sviluppo: ["software"] }
export const spesaDaIdea = (categorie) => [...new Set((categorie || []).flatMap((c) => SPESA_DA_IDEA[c] || []))]

export const emptyProfile = () => ({
  settore: "", budgetStimato: "", stadio: "idea", formaGiuridica: "team-non-costituito",
  startupInnovativa: null, regione: "", provincia: "", comune: "", project_id: "", categorieSpesa: [], note: "",
})

export const conScale = (pr) => ({ ...pr, stadio: STAGE_NUOVO[pr.stadio] || pr.stadio || "nd",
  costituita: pr.costituita || triDa(pr.societaCostituita), startup: pr.startup || triDa(pr.startupInnovativa),
  registro: pr.registro || triDa(pr.iscrittaRegistro), dimensione: pr.dimensione || "nd", statoImpresa: pr.statoImpresa || "nd",
  dataCostituzione: pr.dataCostituzione || "", annoCostituzione: pr.annoCostituzione || null })

const DAL_TESTO = ["costituita", "registro", "startup", "dimensione", "statoImpresa", "dataCostituzione"]
const vuotoP = (x) => x == null || x === "" || x === "nd" || (Array.isArray(x) && !x.length)
export function fonteProfilo(k, p, est) {
  if (!est || vuotoP(p[k])) return ""
  if (est._fonti_preanalisi?.[k] && String(p[k]) === String(est[k])) return "Letto dal testo"
  if (est._fonti_online?.[k] && String(p[k]) === String(est._fonti_online[k])) return "Banca dati · confermata"
  const a = p[k], b = est[k]
  const uguale = Array.isArray(a) ? [...a].sort().join() === [...(b || [])].sort().join() : String(a) === String(b ?? "")
  if (vuotoP(b) || !uguale) return "Dichiarato"
  return DAL_TESTO.includes(k) ? "Letto dal testo" : "Proposta AI"
}

// unisciImpresa di components/ImpresaDaPiva.js: senza un'impresa confermata e reale l'estratto resta com'è
export function unisciImpresa(estratto, impresa) {
  if (!impresa?.confermata || !impresa.record || impresa.record.ambiente !== "production") return estratto
  const r = impresa.record, sede = r.dati.sede_legale
  if (estratto.partitaIvaDocumento && estratto.partitaIvaDocumento !== r.dati.partita_iva) return { ...estratto,
    conflittoIdentita: true, note: "Il documento e l’impresa recuperata hanno partite IVA diverse. Torna indietro prima di proseguire." }
  // The idea's investment location remains separate. Copy the registered office only on explicit choice.
  const territorio = impresa.usaSede && sede && !estratto.regione && !estratto.provincia && !estratto.comune
    ? { regione: sede.regione, provincia: sede.sigla, comune: sede.code, comuneNome: sede.comune } : {}
  return { ...estratto, ...territorio, impresaOnlineId: r.id, impresaOnline: r,
    _fonti_online: Object.fromEntries(Object.entries(territorio)) }
}

const FONTE_BASE = "ml-1 normal-case tracking-normal font-medium text-[10px] px-1.5 rounded"
const FONTE_CLS = { "Proposta AI": "bg-[#fff6cc] text-amber-900", "Letto dal testo": "bg-sky-50 text-sky-800" }
const MODO_ON = ["border-zinc-900", "bg-zinc-50"]
const MODO_OFF = ["border-zinc-200", "hover:border-zinc-300"]
const ZONA_ON = ["border-zinc-900", "bg-zinc-50"]
const ZONA_OFF = ["border-zinc-300", "hover:border-zinc-400"]
const SPESA_ON = ["bg-zinc-900", "text-white", "border-zinc-900"]
const SPESA_OFF = ["bg-white", "text-zinc-600", "border-zinc-300", "hover:border-zinc-400"]
const MSG_IMPRESA = "Attendi il recupero e conferma l’impresa, oppure continua senza questi dati."
const SELECT_DEL_PROFILO = ["stadio", "regione", "provincia", "costituita", "registro", "dimensione", "statoImpresa", "startup"]
// DatiAnalisiProgetto (delta C7): i campi di testo; «Letto dal testo: …» solo finché il valore è ancora quello letto
const DATI_TESTO = ["atecoCodice", "atecoClassificazione", "dataAvvioProgetto", "descrizioneSpese"]
const lettoDalTesto = (p, k) => p._fonti_preanalisi?.[k] && p[k] === p._valori_preanalisi?.[k]
// il valore di un campo per il profilo: la durata è un numero o null; «Non so / da verificare» è null, distinto da «No»
const valoreCampo = (el) => {
  const v = el.value
  if (el.dataset.tipo === "numero") return v ? Number(v) : null
  if (el.dataset.tipo === "tri") return v === "si" ? true : v === "no" ? false : null
  return v
}

export default class extends Controller {
  static targets = ["stepper", "passo0", "passo1", "risultati", "confermato",
    "modo", "dropzone", "file", "zonaAttesa", "zonaIcona", "zonaTesto", "ultimoFile", "sfogliaBtn", "testo", "errore", "erroreTesto",
    "continua", "continuaAttesa", "continuaEtichetta", "continuaPronto",
    "nota", "campo", "fonte", "incerto", "comunePicker", "dataNota", "spesa", "legenda", "cerca", "conflitto", "riepilogoImpresa",
    "progetti", "progettoSelect", "progetto", "progettoVoce", "senzaProgetto", "daProgettoNota", "idea", "impresa",
    "letto", "datiGriglia"]
  // stadi: i valori di STADI (Stadi::STADI), gli stessi delle opzioni del menu dello stadio
  static values = { project: String, stadi: Array }

  connect() {
    this.profile = emptyProfile()
    this.sourceNote = ""
    this.estratto = null
    this.impresa = null
    if (this.projectValue) {
      this.profile = { ...emptyProfile(), project_id: this.projectValue }
      this.vai(1)
    } else {
      this.vai(0)
    }
  }

  // ---- passi -----------------------------------------------------------------------------------------------------

  vai(step) {
    const prima = this.step
    this.step = step
    this.stepperTargets.forEach((s) => { s.hidden = Number(s.dataset.passo) !== step })
    this.passo0Target.hidden = step !== 0
    this.passo1Target.hidden = step !== 1
    this.risultatiTarget.hidden = step !== 2
    this.impresaTargets.forEach((s) => {
      s.hidden = step !== 0
      // ImpresaDaPiva torna montata al passo 0: legge di nuovo la configurazione (mai un recupero)
      if (step === 0 && prima !== undefined && prima !== 0) this.application.getControllerForElementAndIdentifier(s, "impresa-da-piva")?.rimonta()
    })
    if (step === 0) this.apriInput()
    if (step === 1) this.apriProfilo()
    // il passo 2 si smonta quando lo si lascia (trova_risultati_controller.js)
    if (prima !== undefined && prima !== step) this.dispatch("passo", { detail: { step } })
  }

  // ---- passo 0: InputStep ------------------------------------------------------------------------------------------

  apriInput() {
    this.mode = "idea"
    this.busy = false
    this.busyLabel = ""
    this.fileName = ""
    this.error = ""
    this.dragOver = false
    this.testoTarget.value = ""
    this.disegnaInput()
  }

  disegnaInput() {
    const doc = this.mode === "document"
    this.modoTargets.forEach((b) => {
      const on = b.dataset.modo === this.mode
      b.classList.remove(...(on ? MODO_OFF : MODO_ON))
      b.classList.add(...(on ? MODO_ON : MODO_OFF))
    })
    this.dropzoneTarget.hidden = !doc
    this.dropzoneTarget.classList.remove(...(this.dragOver ? ZONA_OFF : ZONA_ON))
    this.dropzoneTarget.classList.add(...(this.dragOver ? ZONA_ON : ZONA_OFF))
    this.zonaAttesaTarget.hidden = !this.busy
    this.zonaIconaTarget.hidden = this.busy
    this.zonaTestoTarget.textContent = this.busy ? this.busyLabel : "Trascina qui il file oppure clicca per caricare"
    this.ultimoFileTarget.hidden = !(this.fileName && !this.busy)
    this.ultimoFileTarget.textContent = `Ultimo file: ${this.fileName}`
    this.sfogliaBtnTarget.disabled = this.busy
    this.testoTarget.rows = doc ? 4 : 8
    this.testoTarget.placeholder = doc ? this.testoTarget.dataset.segnapostoDocumento : this.testoTarget.dataset.segnapostoIdea
    this.erroreTarget.hidden = !this.error
    this.erroreTestoTarget.textContent = this.error
    this.continuaTarget.disabled = this.busy || !this.testoTarget.value.trim()
    const attesa = this.busy && !this.fileName
    this.continuaAttesaTarget.hidden = !attesa
    this.continuaProntoTarget.hidden = attesa
    this.continuaEtichettaTarget.textContent = this.busyLabel
  }

  modo(event) {
    this.mode = event.currentTarget.dataset.modo
    this.disegnaInput()
  }

  testoCambiato() {
    this.disegnaInput()
  }

  impresaCambiata(event) {
    this.impresa = event.detail.value
  }

  impresaInAttesa() {
    return this.impresa?.pending || (this.impresa?.record && !this.impresa.confermata)
  }

  async continua() {
    const text = this.testoTarget.value
    if (!text.trim()) return
    if (this.impresaInAttesa()) { this.error = MSG_IMPRESA; this.disegnaInput(); return }
    this.busy = true; this.busyLabel = "Sto leggendo il progetto…"; this.error = ""
    this.disegnaInput()
    try {
      const data = await apiJson("/api/finder/extract", { method: "POST", body: { text } })
      this.busy = false
      this.estratto_(data)
    } catch (e) {
      this.busy = false
      this.error = formatApiErrorDetail(e.detail) || "Non sono riuscito a interpretare il testo. Riprova con qualche dettaglio in più."
      if (this.step === 0) this.disegnaInput()
    }
  }

  trascinaSopra(event) { event.preventDefault(); this.dragOver = true; this.disegnaInput() }

  trascinaFuori() { this.dragOver = false; this.disegnaInput() }

  lascia(event) {
    event.preventDefault()
    this.dragOver = false
    this.disegnaInput()
    this.handleFile(event.dataTransfer?.files?.[0])
  }

  sfoglia() { if (!this.busy) this.fileTarget.click() }

  sfogliaPulsante(event) { event.stopPropagation(); this.fileTarget.click() }

  // il clic sul campo file (anche quello del pulsante) non risale alla zona, che lo ripeterebbe
  fermaClic(event) { event.stopPropagation() }

  scelto(event) { this.handleFile(event.target.files?.[0]) }

  async handleFile(file) {
    if (!file) return
    if (this.impresaInAttesa()) { this.error = MSG_IMPRESA; this.disegnaInput(); return }
    if (file.size > 15 * 1024 * 1024) {
      this.error = "File troppo grande (max 15MB). Carica un documento più piccolo o incolla il testo."
      this.disegnaInput()
      return
    }
    this.fileName = file.name
    this.busy = true; this.busyLabel = `Sto leggendo "${file.name}"…`; this.error = ""
    this.disegnaInput()
    try {
      const fd = new FormData()
      fd.append("file", file)
      const data = await apiJson("/api/finder/extract-file", { method: "POST", body: fd })
      this.busy = false
      this.fileTarget.value = ""
      this.estratto_(data)
    } catch (e) {
      this.busy = false
      this.fileTarget.value = ""
      this.error = formatApiErrorDetail(e.detail) || "Non sono riuscito a leggere il file. Prova con un PDF/DOCX/TXT o incolla il testo."
      if (this.step === 0) this.disegnaInput()
    }
  }

  // handleExtract del componente principale
  estratto_(datiEstratti) {
    const extracted = unisciImpresa(datiEstratti, this.impresa)
    this.profile = { ...emptyProfile(), ...extracted, budgetStimato: extracted.budgetStimato ?? "", categorieSpesa: extracted.categorieSpesa || [] }
    this.sourceNote = extracted.note || ""
    this.estratto = extracted
    this.vai(1)
  }

  // ---- passo 1: ProfileStep ----------------------------------------------------------------------------------------

  apriProfilo() {
    this.p = conScale(this.profile)
    this.est = this.estratto ? conScale({ ...emptyProfile(), ...this.estratto }) : null
    this.daProgetto = false
    this.projects = []
    this.disegnaProfilo()
    const richiesta = (this.richiestaProgetti = apiJson("/api/projects").catch(() => null))
    richiesta.then((data) => {
      if (data === null || richiesta !== this.richiestaProgetti || this.step !== 1) return
      this.projects = Array.isArray(data) ? data : []
      this.disegnaProgetti()
      // opened from step 2 of a project (?project=…): its data at once
      if (this.profile.project_id && this.projects.some((x) => x.id === this.profile.project_id) && !this.daProgetto) {
        this.fromProject(this.profile.project_id)
      } else {
        this.disegnaProfilo()
      }
    })
  }

  province() {
    if (!this._province) {
      const sel = this.selectDi("provincia")
      this._province = [...sel.querySelectorAll("[data-select-target='item']")].map((i) => ({ sigla: i.dataset.value, regione: i.dataset.regione }))
    }
    return this._province
  }

  selectDi(k) {
    return this.campoTargets.find((c) => c.dataset.chiave === k)?.closest("[data-controller~='select']")
  }

  campo(event) {
    const k = event.target.dataset?.chiave
    if (!k || this.step !== 1 || !this.campoTargets.includes(event.target)) return
    const v = valoreCampo(event.target)
    const f = this.p
    if (k === "regione") {
      this.p = { ...f, regione: v, comune: "", provincia: this.province().find((x) => x.sigla === f.provincia)?.regione === v ? f.provincia : "" }
    } else if (k === "provincia") {
      this.p = { ...f, provincia: v, comune: "", regione: this.province().find((x) => x.sigla === v)?.regione || f.regione }
    } else {
      this.p = { ...f, [k]: v }
    }
    this.disegnaProfilo()
  }

  // FiltriOpportunitaProgetto: una scelta dei criteri delle fonti (onChange(key, valori))
  criterio(event) {
    if (this.step !== 1) return
    this.p = { ...this.p, [event.detail.key]: event.detail.value }
    this.disegnaProfilo()
  }

  comune(event) {
    const c = event.detail.comune
    this.p = { ...this.p, comune: c?.code || "", comuneNome: c?.comune || "", ...(c ? { provincia: c.sigla, regione: c.regione } : {}) }
    this.disegnaProfilo()
  }

  spesa(event) {
    const key = event.currentTarget.dataset.spesa
    const f = this.p
    this.p = { ...f, categorieSpesa: f.categorieSpesa.includes(key) ? f.categorieSpesa.filter((c) => c !== key) : [...f.categorieSpesa, key] }
    this.disegnaProfilo()
  }

  progettoScelto(event) {
    const id = event.detail.value
    if (id) this.fromProject(id)
  }

  // the project's data prevail over what the AI deduced from the text (titolare, 08/10/2026)
  fromProject(id) {
    const pr = this.projects.find((x) => x.id === id) || {}
    const sede = pr.sede_operativa
    const budget = (pr.voci || []).reduce((t, v) => t + (Number(v.importo) || 0), 0) || pr.idea?.budget_max || pr.budget || ""
    const daIdea = spesaDaIdea(pr.idea?.categorie)
    const f = this.p
    this.p = {
      ...f, project_id: id, impresaOnlineId: "", impresaOnline: null, _fonti_online: {},
      ...(sede ? { regione: sede.regione, provincia: sede.sigla, comune: sede.code, comuneNome: sede.comune } : {}),
      ...(pr.preanalisi_iniziale || {}),
      atecoCodice: pr.ateco?.codice || "", atecoClassificazione: pr.ateco?.classificazione || "", formaGiuridicaImpresa: pr.forma_giuridica || "",
      ...(pr.sector ? { settore: pr.sector } : {}),
      ...(budget ? { budgetStimato: String(Math.round(budget)) } : {}),
      // a stage not indicated in the project stays not indicated
      stadio: this.stadiValue.includes(pr.stage) ? pr.stage : "nd",
      ...(pr.societa_costituita === true ? { costituita: "si" } : pr.societa_costituita === false ? { costituita: "no" } : {}),
      ...(pr.startup_innovativa === true ? { startup: "si" } : pr.startup_innovativa === false ? { startup: "no" } : {}),
      registro: triDa(pr.iscritta_registro), dimensione: pr.dimensione || "nd", statoImpresa: pr.stato_impresa || "nd",
      dataCostituzione: pr.data_costituzione || "", annoCostituzione: pr.anno_costituzione || null,
      ...(daIdea.length ? { categorieSpesa: daIdea } : {}), idea: pr.idea?.testo || "",
    }
    this.daProgetto = true
    this.disegnaProfilo()
  }

  imposta(k, valore) {
    const input = this.campoTargets.find((c) => c.dataset.chiave === k)
    if (!input || input.value === valore) return
    input.value = valore
    const sel = input.closest("[data-controller~='select']")
    // il menu riallinea l'etichetta al valore del campo (lo stesso evento dei morph)
    sel?.dispatchEvent(new Event("turbo:morph-element"))
  }

  disegnaProfilo() {
    const p = this.p, est = this.est
    this.imposta("settore", p.settore ?? "")
    this.imposta("budgetStimato", p.budgetStimato == null ? "" : String(p.budgetStimato || ""))
    this.imposta("dataCostituzione", p.dataCostituzione)
    SELECT_DEL_PROFILO.forEach((k) => this.imposta(k, p[k] ?? ""))
    const prov = this.selectDi("provincia")
    prov?.querySelectorAll("[data-select-target='item']").forEach((i) => { i.hidden = !!p.regione && i.dataset.regione !== p.regione })
    const picker = this.application.getControllerForElementAndIdentifier(
      this.comunePickerTarget.querySelector("[data-controller~='comune-picker']"), "comune-picker")
    picker?.imposta(p.comune ? { code: p.comune, comune: p.comuneNome || p.comune, sigla: p.provincia, regione: p.regione } : null)
    this.incertoTargets.forEach((s) => { s.hidden = !!p[s.dataset.chiave] })
    this.dataNotaTarget.textContent = p.dataCostituzione ? "" : p.annoCostituzione ? `Non indicata: il testo dice solo «costituita nel ${p.annoCostituzione}».` : "Non indicata."
    this.spesaTargets.forEach((b) => {
      const on = p.categorieSpesa.includes(b.dataset.spesa)
      b.classList.remove(...(on ? SPESA_OFF : SPESA_ON))
      b.classList.add(...(on ? SPESA_ON : SPESA_OFF))
    })
    this.fonteTargets.forEach((s) => {
      const f = fonteProfilo(s.dataset.chiave, p, est)
      s.hidden = !f
      s.textContent = f
      s.className = `${FONTE_BASE} ${FONTE_CLS[f] || "bg-zinc-100 text-zinc-700"}`
    })
    this.legendaTarget.hidden = !est
    this.notaTarget.hidden = !this.sourceNote
    this.notaTarget.textContent = this.sourceNote
    this.disegnaProgetti()
    this.riepilogoImpresaTarget.hidden = !p.impresaOnline
    if (p.impresaOnline) {
      const r = p.impresaOnline
      const valori = { ...r.dati, fonte: r.fonte }
      this.riepilogoImpresaTarget.querySelectorAll("[data-campo]").forEach((el) => { el.textContent = valori[el.dataset.campo] ?? "" })
    }
    this.conflittoTarget.hidden = !p.conflittoIdentita
    this.cercaTarget.disabled = !!p.conflittoIdentita
    this.disegnaDati()
    // FiltriOpportunitaProgetto legge il profilo corrente (filtri_opportunita_controller.js)
    this.dispatch("profilo", { detail: { p } })
  }

  // DatiAnalisiProgetto e lo stato delle opportunità (delta C7)
  disegnaDati() {
    const p = this.p
    DATI_TESTO.forEach((k) => this.imposta(k, p[k] ?? ""))
    this.imposta("formaGiuridicaImpresa", p.formaGiuridicaImpresa || "")
    this.imposta("durataMesi", p.durataMesi == null ? "" : String(p.durataMesi))
    this.imposta("speseGiaAvviate", p.speseGiaAvviate === true ? "si" : p.speseGiaAvviate === false ? "no" : "")
    this.imposta("statoOpportunita", p.statoOpportunita || "attuali")
    this.lettoTargets.forEach((s) => {
      const k = s.dataset.chiave
      const si = lettoDalTesto(p, k)
      s.hidden = !si
      s.textContent = si ? `Letto dal testo: ${p._fonti_preanalisi[k]}` : ""
    })
    const griglia = this.datiGrigliaTarget
    griglia.parentElement.querySelectorAll(":scope > [data-avviso-preanalisi]").forEach((x) => x.remove())
    ;(p.avvisiPreanalisi || []).forEach((a) => {
      const el = document.createElement("p")
      el.className = "text-xs text-amber-800"
      el.dataset.avvisoPreanalisi = ""
      el.textContent = a
      griglia.before(el)
    })
  }

  disegnaProgetti() {
    const p = this.p
    this.progettiTarget.hidden = this.projects.length === 0
    const sel = this.progettoSelectTarget
    const contenuto = sel.querySelector("[data-select-target='content'] > div")
    if (contenuto.dataset.caricati !== String(this.projects.length)) {
      contenuto.replaceChildren(...this.projects.map((x) => {
        const voce = this.progettoVoceTarget.content.firstElementChild.cloneNode(true)
        voce.dataset.value = String(x.id)
        voce.querySelector("[data-item-text]").textContent =
          `${x.name}${x.sede_operativa ? ` · ${x.sede_operativa.comune}` : ""} · #${String(x.id).slice(0, 8)}`
        return voce
      }))
      contenuto.dataset.caricati = String(this.projects.length)
    }
    if (this.progettoTarget.value !== (p.project_id || "")) {
      this.progettoTarget.value = p.project_id || ""
    }
    sel.dispatchEvent(new Event("turbo:morph-element"))
    this.senzaProgettoTarget.hidden = !!p.project_id
    this.daProgettoNotaTarget.hidden = !(p.project_id && this.daProgetto)
    this.daProgettoNotaTarget.textContent = `Settore, budget, stadio, società e sede presi dal progetto${p.idea ? ", categorie di spesa da «Il progetto»" : ""}.`
    this.ideaTarget.hidden = !(p.project_id && this.daProgetto && p.idea)
    this.ideaTarget.textContent = `Il progetto: ${p.idea || ""}`
  }

  indietro() {
    this.vai(0)
  }

  // ---- passo 2: ResultsStep (il catalogo e le azioni sono in trova_risultati_controller.js) ------------------------

  // «Correggi profilo»: il profilo confermato riaperto al passo 1
  correggi() {
    this.vai(1)
  }

  // «Nuova analisi» (onRestart): passo 0 con profilo, nota, estratto e impresa da partita IVA azzerati
  ricomincia() {
    this.profile = emptyProfile()
    this.sourceNote = ""
    this.estratto = null
    this.impresa = null
    this.impresaTargets.forEach((s) => this.application.getControllerForElementAndIdentifier(s, "impresa-da-piva")?.onChange(null))
    this.vai(0)
  }

  cerca() {
    const p = this.p
    if (p.conflittoIdentita) return
    const { costituita, startup, registro, ...rest } = p
    // the search reads the legal form: from the answers given; the text's own reading when not indicated
    const forma = costituita === "nd" ? (rest.formaGiuridica || "team-non-costituito") : formaDa(costituita, startup)
    const confermato = { ...rest, stadio: rest.stadio === "nd" ? "" : rest.stadio, formaGiuridica: forma, societaCostituita: triA(costituita), startupInnovativa: triA(startup),
      iscrittaRegistro: triA(registro), dimensione: p.dimensione === "nd" ? "" : p.dimensione,
      statoImpresa: p.statoImpresa === "nd" ? "" : p.statoImpresa }
    this.profile = confermato
    this.confermatoTarget.textContent = JSON.stringify(confermato)
    this.vai(2)
    this.dispatch("profilo-confermato", { detail: { profile: confermato, estratto: this.estratto } })
  }
}
